import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Clock, X } from 'lucide-react';

const DemoBanner = () => {
  const { user } = useAuth();
  const [remainingSec, setRemainingSec] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.isDemo || !user?.demoSessionExpiresAt || dismissed) return;
    const update = () => {
      const sec = Math.max(0, Math.floor((user.demoSessionExpiresAt - Date.now()) / 1000));
      setRemainingSec(sec);
    };
    update(); // Run immediately so banner shows right away
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [user?.isDemo, user?.demoSessionExpiresAt, dismissed]);

  // Update document title with demo timer when visible (shows in browser tab)
  useEffect(() => {
    if (!user?.isDemo || dismissed) {
      document.title = 'WealthCore';
      return;
    }
    const baseTitle = 'WealthCore';
    const mins = remainingSec !== null ? Math.floor(remainingSec / 60) : (user?.demoSessionExpiresAt ? Math.floor(Math.max(0, (user.demoSessionExpiresAt - Date.now()) / 1000) / 60) : null);
    const secs = remainingSec !== null ? remainingSec % 60 : (user?.demoSessionExpiresAt ? Math.max(0, Math.floor((user.demoSessionExpiresAt - Date.now()) / 1000)) % 60 : null);
    const timeStr = mins !== null && secs !== null ? `${mins}:${secs.toString().padStart(2, '0')}` : null;
    document.title = timeStr ? `${baseTitle} – Demo ${timeStr} left` : `${baseTitle} – Demo session`;
    return () => { document.title = baseTitle; };
  }, [user?.isDemo, user?.demoSessionExpiresAt, dismissed, remainingSec]);

  // Compute initial display so we don't wait for useEffect (avoids flash of nothing)
  const displaySec = remainingSec !== null ? remainingSec : (user?.demoSessionExpiresAt ? Math.max(0, Math.floor((user.demoSessionExpiresAt - Date.now()) / 1000)) : null);
  if (!user?.isDemo || dismissed) return null;

  // Show banner for demo users - with timer if we have expiry, otherwise generic message
  const mins = displaySec !== null ? Math.floor(displaySec / 60) : null;
  const secs = displaySec !== null ? displaySec % 60 : null;
  const timeStr = mins !== null && secs !== null ? `${mins}:${secs.toString().padStart(2, '0')}` : null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-4 sticky top-16 z-40 shadow-sm">
      <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
        <Clock className="h-4 w-4 flex-shrink-0" />
        <span>
          {timeStr !== null ? (
            <>Demo session: <strong className="text-amber-900">{timeStr}</strong> remaining. Limited to 2 stock analyses. Bulk analysis disabled.</>
          ) : (
            <>Demo session active. Limited to 2 stock analyses. Bulk analysis disabled.</>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded hover:bg-amber-100 text-amber-600"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default DemoBanner;
